/**
 * 图库页（T11b）可组件元数据。
 *
 * 与「会话列表页 / 记忆页」同一约定：本文件声明每个可见部件的
 * id / 角色 / 中文名 / 390x844 逻辑坐标 / zIndex / props / notes，
 * 页面组件通过 `galleryComponentMeta()` 取同一份数据用 `useComponentMeta()` 注册，
 * `scripts/generate-manifest.ts` 亦在采集阶段引用本文件，保证清单与页面坐标一致。
 *
 * 坐标原点为画布左上角，内容区自 y=24（状态栏安全区）起。
 */
import type { ComponentMeta } from '../../core/types';
import { buildComponentMeta, type BoxLayout, type ComponentMetaInput } from '../../ui/meta';

/** 内容区顶部（与 app.content 对齐）。 */
export const PAGE_TOP = 24;
/** 内容区底部（底部导航条之上）。 */
export const PAGE_BOTTOM = 768;

export const HEADER_TOP = 24;
export const HEADER_HEIGHT = 56;
/** 三列缩略图网格：顶部 / 左右内边距 / 列间距 / 图片边长 / 图片下方时间条高度。 */
export const GRID_TOP = 88;
export const GRID_PADDING = 12;
export const GRID_GAP = 8;
export const GRID_COLUMNS = 3;
export const CELL_SIZE = 116;
export const CAPTION_HEIGHT = 22;
export const CELL_HEIGHT = CELL_SIZE + CAPTION_HEIGHT;
/** 日期分组标题高度与组间距（每天另起一行展示）。 */
export const GROUP_HEADER_HEIGHT = 30;
export const GROUP_GAP = 12;
/** 可点元素最小尺寸（安卓触摸目标）。 */
export const TOUCH = 44;

/** 一个日期分组的声明布局：标题 + 组内各缩略图的画布绝对坐标。 */
export interface GalleryGroupLayout {
  /** 分组标题顶边（画布坐标）。 */
  top: number;
  /** 日期标题容器（日期 + 张数）。 */
  header: BoxLayout;
  /** 组内每个缩略图（含下方时间条）的布局，按行优先排列。 */
  items: BoxLayout[];
  /** 分组总高度（标题 + 所有行，不含组间距）。 */
  height: number;
}

export interface GalleryLayoutPlan {
  groups: GalleryGroupLayout[];
  /** 整个分组列表的高度，用于撑开滚动容器。 */
  height: number;
}

/** 日期标题容器：通栏，文字左右各留 `GRID_PADDING`。 */
export function galleryGroupHeaderLayout(top: number): BoxLayout {
  return { x: 0, y: top, width: 390, height: GROUP_HEADER_HEIGHT };
}

/** 日期标题左侧文案盒（`2026年10月19日 周一`）。 */
export function galleryDayLabelLayout(header: BoxLayout): BoxLayout {
  return { x: GRID_PADDING, y: header.y + 4, width: 280, height: 22 };
}

/** 日期标题右侧张数盒（`3 张`）。 */
export function galleryDayCountLayout(header: BoxLayout): BoxLayout {
  return {
    x: header.x + header.width - GRID_PADDING - 72,
    y: header.y + 4,
    width: 72,
    height: 22,
  };
}

/** 某日期分组内第 index 个缩略图的声明布局（3 列，组内换行）。 */
export function galleryItemLayout(groupTop: number, index: number): BoxLayout {
  const col = index % GRID_COLUMNS;
  const row = Math.floor(index / GRID_COLUMNS);
  return {
    x: GRID_PADDING + col * (CELL_SIZE + GRID_GAP),
    y: groupTop + GROUP_HEADER_HEIGHT + row * (CELL_HEIGHT + GRID_GAP),
    width: CELL_SIZE,
    height: CELL_HEIGHT,
  };
}

/** 缩略图按钮铺满图片区域（不含下方时间条）。 */
export function galleryItemThumbLayout(item: BoxLayout): BoxLayout {
  return { x: item.x, y: item.y, width: item.width, height: CELL_SIZE };
}

/** 缩略图下方的时间条（生成日期 + 时间）。 */
export function galleryItemTimeLayout(item: BoxLayout): BoxLayout {
  return { x: item.x, y: item.y + CELL_SIZE, width: item.width, height: CAPTION_HEIGHT };
}

/** 图片区域左下角收藏按钮。 */
export function galleryItemFavoriteLayout(item: BoxLayout): BoxLayout {
  return { x: item.x + 4, y: item.y + CELL_SIZE - TOUCH - 4, width: TOUCH, height: TOUCH };
}

/** 图片区域右下角删除按钮。 */
export function galleryItemDeleteLayout(item: BoxLayout): BoxLayout {
  return {
    x: item.x + item.width - TOUCH - 4,
    y: item.y + CELL_SIZE - TOUCH - 4,
    width: TOUCH,
    height: TOUCH,
  };
}

/** 一个日期分组的总高度：标题 + N 行图片 + 行间距。 */
export function galleryGroupHeight(itemCount: number): number {
  const rows = Math.max(1, Math.ceil(itemCount / GRID_COLUMNS));
  return GROUP_HEADER_HEIGHT + rows * CELL_HEIGHT + (rows - 1) * GRID_GAP;
}

/**
 * 按「每天一组」排布整个图库：日期标题占一行，组内图片三列换行，
 * 一天结束后空出 `GROUP_GAP` 再开始下一天 —— 这就是「以日期换行」的布局来源。
 *
 * 纯函数，只依赖每组图片张数；页面与组件清单调用同一份数据，避免两处坐标漂移。
 */
export function planGalleryLayout(counts: readonly number[]): GalleryLayoutPlan {
  const groups: GalleryGroupLayout[] = [];
  let cursor = GRID_TOP;

  for (const count of counts) {
    const top = cursor;
    const height = galleryGroupHeight(count);
    groups.push({
      top,
      header: galleryGroupHeaderLayout(top),
      items: Array.from({ length: count }, (_, index) => galleryItemLayout(top, index)),
      height,
    });
    cursor = top + height + GROUP_GAP;
  }

  return {
    groups,
    height: groups.length === 0 ? 0 : cursor - GROUP_GAP - GRID_TOP,
  };
}

/** 构造一条「图库页」组件元数据输入（统一填 page 字段）。 */
export function galleryMetaInput(
  id: string,
  label: string,
  role: ComponentMeta['role'],
  layout: BoxLayout,
  options: { zIndex?: number; props?: string[]; notes?: string } = {},
): ComponentMetaInput {
  return {
    id,
    page: 'gallery',
    label,
    role,
    layout,
    zIndex: options.zIndex ?? 0,
    ...(options.props === undefined ? {} : { props: options.props }),
    ...(options.notes === undefined ? {} : { notes: options.notes }),
  };
}

const TEMPLATE_PLAN = planGalleryLayout([1]);
const TEMPLATE_GROUP = TEMPLATE_PLAN.groups[0];
if (TEMPLATE_GROUP === undefined) throw new Error('[gallery] 模板分组缺失');
const CELL0 = TEMPLATE_GROUP.items[0];
if (CELL0 === undefined) throw new Error('[gallery] 模板缩略项缺失');

export const GALLERY_COMPONENTS: ComponentMetaInput[] = [
  galleryMetaInput('page.gallery.root', '图库页容器', 'container', {
    x: 0,
    y: PAGE_TOP,
    width: 390,
    height: PAGE_BOTTOM - PAGE_TOP,
  }, { zIndex: 2, notes: '页面根容器（含顶部 24dp 状态栏安全区偏移）；安卓用 Fragment 根 View' }),
  galleryMetaInput('gallery.header', '图库页头部', 'container', {
    x: 0,
    y: HEADER_TOP,
    width: 390,
    height: HEADER_HEIGHT,
  }, { zIndex: 3, notes: '标题 + 清空按钮容器；安卓用 MaterialToolbar' }),
  galleryMetaInput('gallery.header.title', '页面标题：图库', 'text', {
    x: 16,
    y: HEADER_TOP + 12,
    width: 220,
    height: 32,
  }, { zIndex: 4, notes: '安卓用 Toolbar 标题（单行，超出省略）' }),
  galleryMetaInput('gallery.clearButton', '清空图库', 'button', {
    x: 286,
    y: HEADER_TOP + 8,
    width: 88,
    height: TOUCH,
  }, { zIndex: 4, props: ['disabled'], notes: '危险操作：清空需二次确认；安卓用 Toolbar 菜单 / TextButton' }),
  galleryMetaInput('gallery.grid', '按日期分组的缩略图列表', 'list', {
    x: 0,
    y: GRID_TOP,
    width: 390,
    height: PAGE_BOTTOM - GRID_TOP,
  }, { zIndex: 3, props: ['items'], notes: '安卓用 RecyclerView：每天一个日期标题 ViewType（span 占满），组内 GridLayoutManager(span=3) 换行' }),
  galleryMetaInput('gallery.dateHeader', '日期分组标题（模板）', 'container', {
    x: TEMPLATE_GROUP.header.x,
    y: TEMPLATE_GROUP.header.y,
    width: TEMPLATE_GROUP.header.width,
    height: TEMPLATE_GROUP.header.height,
  }, { zIndex: 5, props: ['date', 'count'], notes: '每天一行日期标题；运行时按日期展开为 gallery.dateHeader.<YYYY-MM-DD>' }),
  galleryMetaInput('gallery.dateHeader.label', '日期分组日期文案', 'text', {
    ...galleryDayLabelLayout(TEMPLATE_GROUP.header),
  }, { zIndex: 6, props: ['date'], notes: '`2026年10月19日 周一`，安卓用 TextView' }),
  galleryMetaInput('gallery.dateHeader.count', '日期分组张数', 'text', {
    ...galleryDayCountLayout(TEMPLATE_GROUP.header),
  }, { zIndex: 6, props: ['count'], notes: '当天图片张数，右对齐' }),
  galleryMetaInput('gallery.item', '图库缩略项（模板）', 'container', {
    x: CELL0.x,
    y: CELL0.y,
    width: CELL0.width,
    height: CELL0.height,
  }, { zIndex: 4, props: ['prompt', 'model', 'createdAt', 'favorite'], notes: '网格项模板（图片 + 下方时间条）；运行时按图片 id 展开为 gallery.item.<id>' }),
  galleryMetaInput('gallery.item.thumb', '缩略图按钮', 'button', {
    ...galleryItemThumbLayout(CELL0),
  }, { zIndex: 5, props: ['src'], notes: '点击进入大图预览；下方时间条不参与点击' }),
  galleryMetaInput('gallery.item.time', '缩略图生成时间', 'text', {
    ...galleryItemTimeLayout(CELL0),
  }, { zIndex: 5, props: ['createdAt'], notes: '`MM-DD HH:mm`；安卓用 TextView，tabular-nums' }),
  galleryMetaInput('gallery.item.favoriteButton', '收藏 / 取消收藏', 'button', {
    ...galleryItemFavoriteLayout(CELL0),
  }, { zIndex: 6, props: ['favorite'], notes: '切换收藏状态' }),
  galleryMetaInput('gallery.item.deleteButton', '删除单张', 'button', {
    ...galleryItemDeleteLayout(CELL0),
  }, { zIndex: 6, notes: '删除该图（无需二次确认，可重新生成）' }),
  galleryMetaInput('gallery.empty', '图库空状态', 'container', {
    x: 0,
    y: 300,
    width: 390,
    height: 160,
  }, { zIndex: 3, notes: '本地没有图片时显示' }),
  galleryMetaInput('gallery.empty.title', '空状态主文案', 'text', {
    x: 0,
    y: 320,
    width: 390,
    height: 32,
  }, { zIndex: 4, notes: '居中' }),
  galleryMetaInput('gallery.empty.hint', '空状态说明', 'text', {
    x: 16,
    y: 360,
    width: 358,
    height: 44,
  }, { zIndex: 4, notes: '居中，次要文本色' }),
  galleryMetaInput('gallery.previewBackdrop', '大图预览遮罩', 'modal', {
    x: 0,
    y: PAGE_TOP,
    width: 390,
    height: PAGE_BOTTOM - PAGE_TOP,
  }, { zIndex: 50, notes: '大图预览弹层遮罩' }),
  galleryMetaInput('gallery.preview', '大图预览卡片', 'modal', {
    x: 16,
    y: 96,
    width: 358,
    height: 560,
  }, { zIndex: 51, notes: '展示大图 / prompt / 模型 / 生成时间' }),
  galleryMetaInput('gallery.preview.image', '大图', 'container', {
    x: 32,
    y: 116,
    width: 326,
    height: 300,
  }, { zIndex: 52, props: ['src'], notes: '等比适配的完整图片' }),
  galleryMetaInput('gallery.preview.closeButton', '关闭预览', 'button', {
    x: 326,
    y: 104,
    width: TOUCH,
    height: TOUCH,
  }, { zIndex: 53, notes: '关闭大图预览' }),
  galleryMetaInput('gallery.preview.prompt', '预览提示词', 'text', {
    x: 32,
    y: 424,
    width: 326,
    height: 44,
  }, { zIndex: 52, notes: '两行省略' }),
  galleryMetaInput('gallery.preview.meta', '预览元信息', 'text', {
    x: 32,
    y: 470,
    width: 326,
    height: 18,
  }, { zIndex: 52, notes: '模型 · MIME' }),
  galleryMetaInput('gallery.preview.time', '预览生成时间', 'text', {
    x: 32,
    y: 490,
    width: 326,
    height: 18,
  }, { zIndex: 52, props: ['createdAt'], notes: '`生成时间：YYYY-MM-DD HH:mm`，安卓用 TextView' }),
  galleryMetaInput('gallery.preview.favoriteButton', '预览内收藏 / 取消收藏', 'button', {
    x: 32,
    y: 512,
    width: 150,
    height: TOUCH,
  }, { zIndex: 52, props: ['favorite'], notes: '切换收藏状态' }),
  galleryMetaInput('gallery.preview.deleteButton', '预览内删除', 'button', {
    x: 190,
    y: 512,
    width: 150,
    height: TOUCH,
  }, { zIndex: 52, notes: '删除该图并关闭预览' }),
  galleryMetaInput('gallery.clearConfirm.backdrop', '清空确认遮罩', 'background', {
    x: 0,
    y: PAGE_TOP,
    width: 390,
    height: PAGE_BOTTOM - PAGE_TOP,
  }, { zIndex: 60, notes: '清空图库二次确认遮罩' }),
  galleryMetaInput('gallery.clearConfirm.dialog', '清空确认弹窗', 'modal', {
    x: 39,
    y: 320,
    width: 312,
    height: 180,
  }, { zIndex: 61, notes: '二次确认对话框' }),
  galleryMetaInput('gallery.clearConfirm.title', '清空确认标题', 'text', {
    x: 63,
    y: 336,
    width: 264,
    height: 28,
  }, { zIndex: 62, notes: '清空图库二次确认标题；安卓用 AlertDialog 标题 TextView' }),
  galleryMetaInput('gallery.clearConfirm.message', '清空确认说明', 'text', {
    x: 63,
    y: 372,
    width: 264,
    height: 44,
  }, { zIndex: 62, notes: '提示清空不可恢复' }),
  galleryMetaInput('gallery.clearConfirm.cancelButton', '取消清空', 'button', {
    x: 63,
    y: 436,
    width: 120,
    height: TOUCH,
  }, { zIndex: 62, notes: '次按钮' }),
  galleryMetaInput('gallery.clearConfirm.confirmButton', '确认清空', 'button', {
    x: 207,
    y: 436,
    width: 120,
    height: TOUCH,
  }, { zIndex: 62, props: ['disabled'], notes: '危险按钮' }),
  galleryMetaInput('gallery.errorBanner', '错误提示条', 'container', {
    x: 12,
    y: 708,
    width: 366,
    height: TOUCH,
  }, { zIndex: 40, notes: '读写失败时显示' }),
  galleryMetaInput('gallery.errorBanner.message', '错误提示文案', 'text', {
    x: 20,
    y: 716,
    width: 350,
    height: 28,
  }, { zIndex: 41, notes: '危险色文本' }),
];

const COMPONENT_INDEX = new Map(GALLERY_COMPONENTS.map((meta) => [meta.id, meta]));

/** 按 id 取声明式元数据输入。 */
export function galleryMeta(id: string): ComponentMetaInput {
  const found = COMPONENT_INDEX.get(id);
  if (found === undefined) throw new Error(`[gallery] 未登记的部件：${id}`);
  return found;
}

/** 按 id 取声明布局（页面内联定位与清单坐标共用一份数据）。 */
export function galleryLayout(id: string): BoxLayout {
  return galleryMeta(id).layout;
}

/** 按 id 构造完整 ComponentMeta，供 `useComponentMeta()` 注册。 */
export function galleryComponentMeta(id: string): ComponentMeta {
  return buildComponentMeta(galleryMeta(id));
}
